import { Switch, Route, Redirect } from "wouter";
import { QueryClientProvider } from "@tanstack/react-query";
import { queryClient } from "./lib/queryClient";
import { Toaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";
import { AuthProvider, useAuth } from "@/hooks/use-auth";
import { ProtectedRoute } from "./lib/protected-route";
import { lazy, Suspense, useEffect } from "react";
import { initializeStorage, initializeVideoStorage, siteGraphicsService } from "@/lib/storage-service";
import { useToast } from "@/hooks/use-toast";
import { LoadingIndicator } from "@/components/LoadingIndicator";
import { RedirectHandler } from "./lib/redirect-handler";

// Lazy load pages
const HomePage = lazy(() => import("@/pages/home-page"));
const AuthPage = lazy(() => import("@/pages/auth-page"));
const SchedulePage = lazy(() => import("@/pages/schedule-page"));
const RoutePage = lazy(() => import("@/pages/route-page"));
const GalleryPage = lazy(() => import("@/pages/gallery-page"));
const AdminPage = lazy(() => import("@/pages/admin-page"));
const ProfilePage = lazy(() => import("@/pages/profile-page"));
const FirstTimerGuidePage = lazy(() => import("@/pages/first-timer-guide-page"));
const VehiclePreparationPage = lazy(() => import("@/pages/vehicle-preparation-page"));
const NotFound = lazy(() => import("@/pages/not-found"));

function CommunityRedirect() {
  const { user, isLoading } = useAuth();
  
  if (isLoading) {
    return <div className="flex items-center justify-center min-h-screen">
      <LoadingIndicator size="large" />
    </div>;
  }
  
  // Redirect to route page if logged in, otherwise to auth page
  return <Redirect to={user ? "/route" : "/auth"} />;
}

function Router() {
  // Storage initialization is now handled globally in App.tsx
  // No need for a separate wrapper component

  return (
    <Suspense fallback={<div className="flex items-center justify-center min-h-screen"><LoadingIndicator size="large" /></div>}>
      {/* Add the RedirectHandler to process ?redirect= parameters */}
      <RedirectHandler />
      
      <Switch>
        <Route path="/" component={HomePage} />
        <Route path="/auth" component={AuthPage} />
        <Route path="/schedule" component={SchedulePage} />
        <Route path="/route" component={RoutePage} />
        <Route path="/gallery" component={GalleryPage} />
        <Route path="/community" component={CommunityRedirect} />
        <Route path="/admin">
          <ProtectedRoute path="/admin" component={AdminPage} />
        </Route>
        <Route path="/profile">
          <ProtectedRoute path="/profile" component={ProfilePage} />
        </Route>
        <Route path="/first-timer-guide" component={FirstTimerGuidePage} />
        <Route path="/vehicle-preparation" component={VehiclePreparationPage} />
        <Route component={NotFound} />
      </Switch>
    </Suspense>
  );
}

function App() {
  const { toast } = useToast();

  // Initialize Supabase storage when the app loads - once per app session
  useEffect(() => {
    const setupStorage = async () => {
      try {
        // Initialize photo storage bucket
        const photoResult = await initializeStorage();
        
        if (photoResult.success) {
          // Only log and show messages if this wasn't a repeat initialization
          if (!photoResult.alreadyInitialized) {
            console.log('Photo storage initialization completed');
            
            // If there's a warning but it still succeeded, show a less severe toast
            if (photoResult.warning) {
              console.warn('Photo storage warning:', photoResult.warning);
              toast({
                title: "Photo Storage Access Limited",
                description: "Photos may be unavailable until RLS policies are configured. See docs/supabaseai.md for setup instructions.",
                variant: "default"
              });
            }
          }
        } else {
          console.error('Failed to initialize photo storage:', photoResult.error);
          toast({
            title: "Photo Storage Initialization Error",
            description: "Photo functionality is limited. Please see docs/supabaseai.md for troubleshooting.",
            variant: "destructive"
          });
        }

        // Initialize video storage bucket
        const videoResult = await initializeVideoStorage();
        
        if (videoResult.success) {
          // Only log and show messages if this wasn't a repeat initialization
          if (!videoResult.alreadyInitialized) {
            console.log('Video storage initialization completed');
            
            if (videoResult.warning) {
              console.warn('Video storage warning:', videoResult.warning);
              toast({
                title: "Video Storage Access Limited",
                description: "Videos may be unavailable until RLS policies are configured.",
                variant: "default"
              });
            }
          }
        } else {
          console.error('Failed to initialize video storage:', videoResult.error);
          toast({
            title: "Video Storage Initialization Error",
            description: "Video functionality is limited.",
            variant: "destructive"
          });
        }

        // Initialize site graphics bucket
        const graphicsResult = await siteGraphicsService.initialize();
        
        if (graphicsResult.success) {
          // Only log if this wasn't a repeat initialization
          if (!graphicsResult.alreadyInitialized) {
            console.log('Site graphics storage initialization completed');
          }
        } else {
          // Only show warning if this wasn't a repeat initialization
          if (!graphicsResult.alreadyInitialized) {
            console.warn('Site graphics storage warning:', graphicsResult.warning || graphicsResult.error);
            toast({
              title: "Site Graphics Access Limited",
              description: "Some site graphics may not display correctly. Please check Supabase storage settings.",
              variant: "default"
            });
          }
        }
      } catch (error) {
        console.error('Failed to initialize storage:', error);
        toast({
          title: "Storage Initialization Error",
          description: "Storage functionality is limited. Please see docs/supabaseai.md for troubleshooting.",
          variant: "destructive"
        });
      }
    };

    setupStorage();
  }, [toast]);

  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <TooltipProvider>
          <Toaster />
          <Router />
          {/* Removed InstallPrompt component */}
        </TooltipProvider>
      </AuthProvider>
    </QueryClientProvider>
  );
}

export default App;